import React, { useState, useEffect } from 'react';
import axios from 'axios';
axios.defaults.withCredentials = true;
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Users, Package, DollarSign, CreditCard, Loader2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

const statusBadge = (status) => {
  const map = {
    active:    'bg-green-100 text-green-800',
    pending:   'bg-yellow-100 text-yellow-800',
    suspended: 'bg-red-100 text-red-800',
    inactive:  'bg-gray-100 text-gray-600',
    approved:  'bg-green-100 text-green-800',
    rejected:  'bg-red-100 text-red-800',
    paid:      'bg-blue-100 text-blue-800',
    unpaid:    'bg-yellow-100 text-yellow-800',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
};

const AffiliateManagement = () => {
  const [selectedTab, setSelectedTab] = useState('affiliates');
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({});
  const [affiliates, setAffiliates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  // inline edit state for packages
  const [pkgEdits, setPkgEdits] = useState({});
  // withdrawal note state
  const [wdNotes, setWdNotes] = useState({});

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, affRes, pkgRes, comRes, wdRes] = await Promise.all([
        axios.get(`${API}/affiliate/admin/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/affiliate/admin/affiliates`).catch(() => ({ data: [] })),
        axios.get(`${API}/affiliate/admin/packages`).catch(() => ({ data: [] })),
        axios.get(`${API}/affiliate/admin/commissions`).catch(() => ({ data: [] })),
        axios.get(`${API}/affiliate/admin/withdrawals`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setAffiliates(Array.isArray(affRes.data) ? affRes.data : []);
      setPackages(Array.isArray(pkgRes.data) ? pkgRes.data : []);
      setCommissions(Array.isArray(comRes.data) ? comRes.data : []);
      setWithdrawals(Array.isArray(wdRes.data) ? wdRes.data : []);
    } catch (err) {
      toast.error('Gagal memuat data affiliate');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const updateAffiliateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/affiliate/admin/affiliates/${id}/status`, { status });
      toast.success('Status diperbarui');
      setAffiliates(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    }
  };

  const updatePackageAffiliate = async (pkg) => {
    const edit = pkgEdits[pkg.id] || {};
    const payload = {
      affiliate_enabled: edit.affiliate_enabled ?? pkg.affiliate_enabled,
      commission_type:   edit.commission_type   ?? pkg.commission_type ?? 'percent',
      commission_value:  parseFloat(edit.commission_value  ?? pkg.commission_value ?? 0),
    };
    try {
      await axios.put(`${API}/affiliate/admin/packages/${pkg.id}/affiliate`, payload);
      toast.success('Paket diperbarui');
      setPackages(prev => prev.map(p => p.id === pkg.id ? { ...p, ...payload } : p));
      setPkgEdits(prev => { const n = { ...prev }; delete n[pkg.id]; return n; });
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    }
  };

  const updateWithdrawalStatus = async (id, status) => {
    try {
      await axios.put(`${API}/affiliate/admin/withdrawals/${id}/status`, { status, note: wdNotes[id] || '' });
      toast.success('Status penarikan diperbarui');
      setWithdrawals(prev => prev.map(w => w.id === id ? { ...w, status } : w));
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    }
  };

  const tabs = [
    { key: 'affiliates', label: 'Affiliates', icon: Users },
    { key: 'produk',     label: 'Produk',     icon: Package },
    { key: 'komisi',     label: 'Komisi',     icon: DollarSign },
    { key: 'penarikan',  label: 'Penarikan',  icon: CreditCard },
  ];

  if (loading) return <div className="text-center py-8 text-muted-foreground">Memuat data affiliate...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Manajemen Affiliate</h1>
        <Button onClick={fetchAll} variant="outline" className="rounded-full">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Total Affiliate', value: stats.total_affiliates ?? 0 },
          { label: 'Aktif',           value: stats.active_affiliates ?? 0 },
          { label: 'Total Klik',      value: stats.total_clicks ?? 0 },
          { label: 'Komisi Terbayar', value: fmt(stats.paid_commissions) },
          { label: 'Komisi Pending',  value: fmt(stats.pending_commissions) },
        ].map(({ label, value }) => (
          <Card key={label} className="rounded-2xl">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-xl font-bold mt-1">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tab buttons */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map(({ key, label, icon: Icon }) => (
          <Button
            key={key}
            variant={selectedTab === key ? 'default' : 'outline'}
            className="rounded-full"
            onClick={() => setSelectedTab(key)}
          >
            <Icon className="h-4 w-4 mr-1" /> {label}
          </Button>
        ))}
      </div>

      <div className="space-y-6">

        {/* Tab: AFFILIATES */}
        {selectedTab === 'affiliates' && (
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Daftar Affiliate</CardTitle>
            </CardHeader>
            <CardContent>
              {affiliates.length === 0
                ? <p className="text-sm text-muted-foreground">Belum ada affiliate</p>
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nama</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tanggal Daftar</TableHead>
                        <TableHead>Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {affiliates.map(a => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium">{a.name || a.full_name || '-'}</TableCell>
                          <TableCell>{a.email}</TableCell>
                          <TableCell>{statusBadge(a.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {a.created_at ? new Date(a.created_at).toLocaleDateString('id-ID') : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {['active', 'suspended', 'inactive'].map(s => (
                                <Button
                                  key={s}
                                  size="sm"
                                  variant={a.status === s ? 'default' : 'outline'}
                                  className="rounded-full text-xs h-7 px-2"
                                  onClick={() => updateAffiliateStatus(a.id, s)}
                                  disabled={a.status === s}
                                >
                                  {s}
                                </Button>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              }
            </CardContent>
          </Card>
        )}

        {/* Tab: PRODUK */}
        {selectedTab === 'produk' && (
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5" /> Produk & Komisi</CardTitle>
            </CardHeader>
            <CardContent>
              {packages.length === 0
                ? <p className="text-sm text-muted-foreground">Belum ada paket</p>
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nama Paket</TableHead>
                        <TableHead>Harga</TableHead>
                        <TableHead>Affiliate</TableHead>
                        <TableHead>Tipe Komisi</TableHead>
                        <TableHead>Nilai Komisi</TableHead>
                        <TableHead>Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {packages.map(pkg => {
                        const edit = pkgEdits[pkg.id] || {};
                        const enabled     = edit.affiliate_enabled  ?? pkg.affiliate_enabled;
                        const comType     = edit.commission_type     ?? pkg.commission_type ?? 'percent';
                        const comValue    = edit.commission_value    ?? pkg.commission_value ?? 0;
                        const dirty = pkg.id in pkgEdits;
                        return (
                          <TableRow key={pkg.id}>
                            <TableCell className="font-medium">{pkg.name}</TableCell>
                            <TableCell>{fmt(pkg.price)}</TableCell>
                            <TableCell>
                              <button
                                type="button"
                                onClick={() => setPkgEdits(prev => ({
                                  ...prev,
                                  [pkg.id]: { ...prev[pkg.id], affiliate_enabled: !enabled }
                                }))}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-gray-300'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </TableCell>
                            <TableCell>
                              <select
                                value={comType}
                                onChange={e => setPkgEdits(prev => ({ ...prev, [pkg.id]: { ...prev[pkg.id], commission_type: e.target.value } }))}
                                className="rounded-lg border border-border p-1 bg-background text-sm"
                              >
                                <option value="percent">Persen (%)</option>
                                <option value="nominal">Nominal (Rp)</option>
                              </select>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-24 h-8 text-sm"
                                value={comValue}
                                onChange={e => setPkgEdits(prev => ({ ...prev, [pkg.id]: { ...prev[pkg.id], commission_value: e.target.value } }))}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                className="rounded-full text-xs h-7 px-3"
                                onClick={() => updatePackageAffiliate(pkg)}
                                disabled={!dirty}
                              >
                                Simpan
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )
              }
            </CardContent>
          </Card>
        )}

        {/* Tab: KOMISI */}
        {selectedTab === 'komisi' && (
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5" /> Riwayat Komisi</CardTitle>
            </CardHeader>
            <CardContent>
              {commissions.length === 0
                ? <p className="text-sm text-muted-foreground">Belum ada komisi</p>
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Affiliate</TableHead>
                        <TableHead>Paket</TableHead>
                        <TableHead>Order</TableHead>
                        <TableHead>Jumlah Komisi</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tanggal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commissions.map(c => (
                        <TableRow key={c.id}>
                          <TableCell>{c.affiliate_name || c.affiliate_email || c.affiliate_id}</TableCell>
                          <TableCell>{c.package_name || c.package_id}</TableCell>
                          <TableCell className="font-mono text-xs">{c.order_id}</TableCell>
                          <TableCell>{fmt(c.amount)}</TableCell>
                          <TableCell>{statusBadge(c.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString('id-ID') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              }
            </CardContent>
          </Card>
        )}

        {/* Tab: PENARIKAN */}
        {selectedTab === 'penarikan' && (
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> Permintaan Penarikan</CardTitle>
            </CardHeader>
            <CardContent>
              {withdrawals.length === 0
                ? <p className="text-sm text-muted-foreground">Belum ada permintaan penarikan</p>
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nama</TableHead>
                        <TableHead>Bank</TableHead>
                        <TableHead>No Rekening</TableHead>
                        <TableHead>Jumlah</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tanggal</TableHead>
                        <TableHead>Catatan</TableHead>
                        <TableHead>Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {withdrawals.map(w => (
                        <TableRow key={w.id}>
                          <TableCell className="font-medium">{w.affiliate_name || w.name || '-'}</TableCell>
                          <TableCell>{w.bank_name}</TableCell>
                          <TableCell className="font-mono text-sm">{w.account_number}</TableCell>
                          <TableCell>{fmt(w.amount)}</TableCell>
                          <TableCell>{statusBadge(w.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {w.created_at ? new Date(w.created_at).toLocaleDateString('id-ID') : '-'}
                          </TableCell>
                          <TableCell>
                            <Input
                              className="w-32 h-7 text-xs"
                              placeholder="Catatan..."
                              value={wdNotes[w.id] || ''}
                              onChange={e => setWdNotes(prev => ({ ...prev, [w.id]: e.target.value }))}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {w.status === 'pending' && (
                                <>
                                  <Button size="sm" className="rounded-full text-xs h-7 px-2 bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => updateWithdrawalStatus(w.id, 'approved')}>
                                    Setuju
                                  </Button>
                                  <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2 text-red-600 border-red-300"
                                    onClick={() => updateWithdrawalStatus(w.id, 'rejected')}>
                                    Tolak
                                  </Button>
                                </>
                              )}
                              {w.status === 'approved' && (
                                <Button size="sm" className="rounded-full text-xs h-7 px-2"
                                  onClick={() => updateWithdrawalStatus(w.id, 'paid')}>
                                  Tandai Dibayar
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              }
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default AffiliateManagement;
